<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="flex flex-column gap-3">
    <div class="form-field">
      <label class="form-label" for="name">{{
        $t("fieldForm.name.label")
      }}</label>
      <PrimeInputText
        id="name"
        v-model="editableField.name"
        :disabled="isEditMode"
        class="w-full"
      />
      <small class="form-help">{{ $t("fieldForm.name.help") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label" for="description">{{
        $t("fieldForm.description.label")
      }}</label>
      <PrimeInputText
        id="description"
        v-model="editableField.description"
        class="w-full"
      />
      <small class="form-help">{{ $t("fieldForm.description.help") }}</small>
    </div>

    <div v-if="editableField.input !== 'parent'" class="form-field">
      <div class="flex align-items-center gap-2">
        <PrimeInputSwitch id="optional" v-model="editableField.optional" />
        <label class="form-label" for="optional">{{
          $t("fieldForm.optional.label")
        }}</label>
      </div>
      <small class="form-help">{{ $t("fieldForm.optional.help") }}</small>
    </div>

    <!-- Default value, prefix, suffix (only for optional fields) -->
    <div
      v-if="editableField.optional"
      class="border-1 surface-border p-3 border-round"
    >
      <h5>{{ $t("fieldForm.optionalSettings.title") }}</h5>
      <div v-if="!showNumberConstraints" class="form-field mb-3">
        <label class="form-label" for="default-value">{{
          $t("fieldForm.defaultValue.label")
        }}</label>
        <PrimeInputText
          id="default-value"
          v-model="editableField.default"
          class="w-full"
        />
        <small class="form-help">{{ $t("fieldForm.defaultValue.help") }}</small>
      </div>
      <div class="grid">
        <div class="col-6">
          <div class="form-field">
            <label class="form-label" for="prefix">{{
              $t("fieldForm.prefix.label")
            }}</label>
            <PrimeInputText
              id="prefix"
              v-model="editableField.prefix"
              class="w-full"
            />
            <small class="form-help">{{ $t("fieldForm.prefix.help") }}</small>
          </div>
        </div>
        <div class="col-6">
          <div class="form-field">
            <label class="form-label" for="suffix">{{
              $t("fieldForm.suffix.label")
            }}</label>
            <PrimeInputText
              id="suffix"
              v-model="editableField.suffix"
              class="w-full"
            />
            <small class="form-help">{{ $t("fieldForm.suffix.help") }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="form-field">
      <label class="form-label" for="type">{{
        $t("fieldForm.dataType.label")
      }}</label>
      <PrimeDropdown
        id="type"
        v-model="editableField.type"
        :options="dataTypes"
        :placeholder="$t('fieldForm.dataType.placeholder')"
        class="w-full"
        @change="onDataTypeChange"
      />
      <small class="form-help">{{ $t("fieldForm.dataType.help") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label" for="input">{{
        $t("fieldForm.inputType.label")
      }}</label>
      <PrimeDropdown
        id="input"
        v-model="editableField.input"
        :options="filteredInputTypes"
        :placeholder="$t('fieldForm.inputType.placeholder')"
        class="w-full"
        @change="onInputTypeChange"
      />
      <small class="form-help">{{ $t("fieldForm.inputType.help") }}</small>
    </div>

    <!-- Source selector for dictionary/lexicon -->
    <div v-if="showSourceSelector" class="form-field">
      <label class="form-label" for="source">{{
        editableField.input === "dictionary"
          ? $t("fieldForm.source.dictionaryLabel")
          : $t("fieldForm.source.lexiconLabel")
      }}</label>
      <PrimeDropdown
        id="source"
        v-model="editableField.source"
        :options="sourceOptions"
        option-label="label"
        option-value="value"
        :placeholder="$t('fieldForm.source.placeholder')"
        class="w-full"
      />
      <small class="form-help">{{ $t("fieldForm.source.help") }}</small>
    </div>

    <!-- String constraints -->
    <div
      v-if="showStringConstraints"
      class="border-1 surface-border p-3 border-round"
    >
      <h5>{{ $t("fieldForm.stringConstraints.title") }}</h5>
      <div class="form-field mb-3">
        <label class="form-label" for="regex">{{
          $t("fieldForm.stringConstraints.regex.label")
        }}</label>
        <PrimeInputText
          id="regex"
          v-model="editableField.regex"
          class="w-full"
        />
        <small class="form-help">{{
          $t("fieldForm.stringConstraints.regex.help")
        }}</small>
      </div>
      <div class="form-field">
        <label class="form-label" for="regex-test">{{
          $t("fieldForm.stringConstraints.regexTester.label")
        }}</label>
        <PrimeInputGroup>
          <PrimeInputText
            id="regex-test"
            v-model="regexTestString"
            :placeholder="
              $t('fieldForm.stringConstraints.regexTester.placeholder')
            "
            class="w-full"
          />
          <PrimeInputGroupAddon :class="regexTestResultClass">
            <i
              class="ri-lg"
              :class="[regexTestResultIcon]"
              aria-hidden="true"
            ></i>
          </PrimeInputGroupAddon>
        </PrimeInputGroup>
      </div>
    </div>

    <!-- Number constraints -->
    <div
      v-if="showNumberConstraints"
      class="border-1 surface-border p-3 border-round"
    >
      <h5>{{ $t("fieldForm.numberConstraints.title") }}</h5>
      <div class="grid">
        <div class="col-4">
          <div class="form-field">
            <label class="form-label" for="default">{{
              $t("fieldForm.numberConstraints.default.label")
            }}</label>
            <PrimeInputNumber
              id="default"
              v-model="editableField.default"
              class="w-full"
            />
          </div>
        </div>
        <div class="col-4">
          <div class="form-field">
            <label class="form-label" for="min">{{
              $t("fieldForm.numberConstraints.min.label")
            }}</label>
            <PrimeInputNumber
              id="min"
              v-model="editableField.min"
              class="w-full"
            />
          </div>
        </div>
        <div class="col-4">
          <div class="form-field">
            <label class="form-label" for="max">{{
              $t("fieldForm.numberConstraints.max.label")
            }}</label>
            <PrimeInputNumber
              id="max"
              v-model="editableField.max"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showValuesInput" class="form-field">
      <label class="form-label" for="values">{{
        $t("fieldForm.values.label")
      }}</label>
      <PrimeTextarea
        id="values"
        v-model="valuesAsString"
        rows="5"
        class="w-full"
      />
      <small class="form-help">{{ $t("fieldForm.values.help") }}</small>
    </div>

    <div
      v-if="showSearchInput"
      class="border-1 surface-border p-3 border-round"
    >
      <h5>{{ $t("fieldForm.search.title") }}</h5>
      <div class="flex flex-column gap-3">
        <div class="form-field">
          <label class="form-label" for="search-lang">{{
            $t("fieldForm.search.lang.label")
          }}</label>
          <PrimeDropdown
            id="search-lang"
            v-model="editableField.search.lang"
            :options="searchLanguages"
            class="w-full"
          />
        </div>
        <div class="form-field">
          <label class="form-label" for="search-query">{{
            $t("fieldForm.search.query.label")
          }}</label>
          <PrimeTextarea
            id="search-query"
            v-model="editableField.search.query"
            rows="3"
            class="w-full"
          />
        </div>
        <div class="form-field">
          <label class="form-label" for="search-value">{{
            $t("fieldForm.search.valueField.label")
          }}</label>
          <PrimeInputText
            id="search-value"
            v-model="editableField.search.valueField"
            class="w-full"
          />
        </div>
        <PrimeButton
          :label="$t('fieldForm.search.testButton.label')"
          icon="ri-search-line"
          class="p-button-outlined"
          @click="testSearch"
          :loading="isTestingSearch"
        />
        <div v-if="testResults.length > 0">
          <h6>{{ $t("fieldForm.search.testResults.title") }}</h6>
          <ul class="list-none p-0 m-0">
            <li v-for="(result, i) in testResults" :key="i">{{ result }}</li>
          </ul>
        </div>
        <PrimeMessage v-if="testError" severity="error">{{
          testError
        }}</PrimeMessage>
      </div>
    </div>

    <div
      v-if="showParentConfig"
      class="border-1 surface-border p-3 border-round"
    >
      <h5>{{ $t("fieldForm.parentConfig.title") }}</h5>
      <div class="form-field">
        <label class="form-label" for="metadata">{{
          $t("fieldForm.parentConfig.metadata.label")
        }}</label>
        <PrimeInputText
          id="metadata"
          v-model="editableField.metadata"
          class="w-full"
        />
        <small class="form-help">{{
          $t("fieldForm.parentConfig.metadata.help")
        }}</small>
      </div>
      <div class="form-field mt-3">
        <label class="form-label" for="fallbackQuery">{{
          $t("fieldForm.parentConfig.fallbackQuery.label")
        }}</label>
        <PrimeTextarea
          id="fallbackQuery"
          v-model="editableField.fallbackQuery"
          class="w-full"
          rows="3"
          autoResize
        />
        <small class="form-help">{{
          $t("fieldForm.parentConfig.fallbackQuery.help")
        }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import InputSwitch from "primevue/inputswitch";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import { searchApi } from "@/AlfrescoApi.js";

const newFieldTemplate = () => ({
  name: "",
  description: "",
  type: "string",
  input: "text",
  values: [],
  search: null,
  regex: null,
  default: null,
  min: null,
  max: null,
  optional: false,
  prefix: null,
  suffix: null,
  metadata: null,
  fallbackQuery: null,
  source: null,
});
const newSearchTemplate = () => ({
  lang: "afts",
  query: "",
  valueField: "cm:name",
});

const typeMapping = {
  string: [
    "text",
    "textarea",
    "password",
    "list",
    "multilist",
    "select",
    "chips",
    "search",
    "dictionary",
    "lexicon",
  ],
  number: ["int", "double", "knob", "rating"],
  boolean: ["boolean", "toggle"],
  date: ["date", "datetime"],
  parent: ["parent"],
};

export default {
  name: "FieldForm",
  components: {
    PrimeInputText: InputText,
    PrimeTextarea: Textarea,
    PrimeDropdown: Dropdown,
    PrimeButton: Button,
    PrimeMessage: Message,
    PrimeInputNumber: InputNumber,
    PrimeInputSwitch: InputSwitch,
    PrimeInputGroup: InputGroup,
    PrimeInputGroupAddon: InputGroupAddon,
  },
  props: {
    field: { type: Object, default: null },
    existingFields: { type: Array, default: () => [] },
    dictionaries: { type: Array, default: () => [] },
    lexicons: { type: Array, default: () => [] },
  },
  emits: ["save"],
  data() {
    return {
      editableField: newFieldTemplate(),
      valuesAsString: "",
      isEditMode: false,
      dataTypes: Object.keys(typeMapping),
      searchLanguages: ["afts", "cmis", "noderef"],
      isTestingSearch: false,
      testResults: [],
      testError: null,
      regexTestString: "",
    };
  },
  computed: {
    filteredInputTypes() {
      return typeMapping[this.editableField.type] || [];
    },
    showValuesInput() {
      return ["list", "multilist", "select"].includes(this.editableField.input);
    },
    showSearchInput() {
      return this.editableField.input === "search";
    },
    showStringConstraints() {
      return (
        this.editableField.type === "string" &&
        this.editableField.input === "text"
      );
    },
    showNumberConstraints() {
      return this.editableField.type === "number";
    },
    showParentConfig() {
      return this.editableField.input === "parent";
    },
    showSourceSelector() {
      return ["dictionary", "lexicon"].includes(this.editableField.input);
    },
    sourceOptions() {
      if (this.editableField.input === "dictionary") {
        return this.dictionaries.map((d) => ({
          label: d.label,
          value: d.id,
        }));
      }
      if (this.editableField.input === "lexicon") {
        return this.lexicons.map((l) => ({
          label: l.label,
          value: l.id,
        }));
      }
      return [];
    },
    regexTestResult() {
      if (!this.editableField.regex || !this.regexTestString) {
        return false;
      }
      try {
        const regex = new RegExp(this.editableField.regex);
        return regex.test(this.regexTestString);
      } catch (e) {
        return false; // Invalid regex
      }
    },
    regexTestResultIcon() {
      return this.regexTestResult ? "ri-check-line" : "ri-close-line";
    },
    regexTestResultClass() {
      return this.regexTestResult ? "bg-green-500" : "bg-red-500";
    },
  },
  watch: {
    field: {
      handler(newVal) {
        if (newVal && newVal.name) {
          this.isEditMode = true;
          this.editableField = {
            ...newFieldTemplate(),
            ...JSON.parse(JSON.stringify(newVal)),
          };
        } else {
          this.isEditMode = false;
          this.editableField = newVal
            ? { ...newFieldTemplate(), ...JSON.parse(JSON.stringify(newVal)) }
            : newFieldTemplate();
        }
        this.valuesAsString = (this.editableField.values || []).join("\n");
        if (!this.editableField.search) {
          this.editableField.search = newSearchTemplate();
        }
      },
      immediate: true,
    },
  },
  methods: {
    onDataTypeChange() {
      if (!this.filteredInputTypes.includes(this.editableField.input)) {
        this.editableField.input = this.filteredInputTypes[0];
      }
    },
    onInputTypeChange() {
      for (const dataType in typeMapping) {
        if (typeMapping[dataType].includes(this.editableField.input)) {
          if (this.editableField.type !== dataType) {
            this.editableField.type = dataType;
          }
          break;
        }
      }
    },
    async testSearch() {
      if (!this.editableField.search.query) {
        this.testError = this.$t("fieldForm.search.testError.emptyQuery");
        return;
      }
      this.isTestingSearch = true;
      this.testResults = [];
      this.testError = null;
      try {
        const query = {
          include: ["properties"],
          query: {
            language: this.editableField.search.lang,
            query: this.editableField.search.query,
          },
        };
        const results = await searchApi.search(query);
        this.testResults = results.list.entries.map(
          (e) =>
            e.entry.properties[this.editableField.search.valueField] ||
            e.entry.name,
        );
        if (this.testResults.length === 0) {
          this.testError = this.$t("fieldForm.search.testError.noResult");
        }
      } catch (e) {
        this.testError = this.$t("fieldForm.search.testError.fetchError", {
          message: e.message,
        });
      } finally {
        this.isTestingSearch = false;
      }
    },
    submit() {
      const finalField = { ...this.editableField };
      if (!finalField.name || !finalField.description) {
        console.error(this.$t("fieldForm.errors.nameAndDescriptionRequired"));
        return;
      }
      if (!this.isEditMode) {
        if (this.existingFields.some((f) => f.name === finalField.name)) {
          console.error(
            this.$t("fieldForm.errors.fieldNameExists", {
              name: finalField.name,
            }),
          );
          return;
        }
      }

      // Cleanup parent fields
      if (this.showParentConfig) {
        delete finalField.values;
        delete finalField.search;
        delete finalField.regex;
        delete finalField.min;
        delete finalField.max;
        delete finalField.default;
        delete finalField.prefix;
        delete finalField.suffix;
        finalField.optional = false;
      } else {
        delete finalField.metadata;
      }

      // Cleanup non-relevant fields
      if (!this.showValuesInput) {
        delete finalField.values;
      } else {
        finalField.values = this.valuesAsString
          ? this.valuesAsString.split("\n").filter((v) => v)
          : [];
      }

      if (!this.showSearchInput) {
        delete finalField.search;
      }

      if (!this.showSourceSelector) {
        delete finalField.source;
      }

      if (!this.showStringConstraints) {
        delete finalField.regex;
      }

      if (!this.showNumberConstraints) {
        delete finalField.min;
        delete finalField.max;
      }

      if (!finalField.input) {
        finalField.input = "text";
      }

      // Cleanup optional-related fields
      if (!finalField.optional) {
        delete finalField.default;
        delete finalField.prefix;
        delete finalField.suffix;
      } else {
        // Keep default as-is (even null for number type)
        // Clean prefix/suffix if empty
        if (!finalField.prefix) {
          delete finalField.prefix;
        }
        if (!finalField.suffix) {
          delete finalField.suffix;
        }
      }

      this.$emit("save", finalField);
    },
  },
};
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-weight: 600;
}

.form-help {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-style: italic;
}
</style>
